import { z } from "zod";

import type { ImportType } from "@web-app/pages/dispatchPage/BulkImportModal/types";
import { AppFilterType } from "@web-app/store/dispatchStore";
import {
  CreateBulkOrdersPayload,
  DeleteBulkOrdersPayload,
  Order,
  UpdateBulkOrdersPayload,
} from "@web-app/types";
import http from "@web-app/utils/http";
import { createOrderRouteType } from "@web-app/utils/order";

export const create = (payload: z.infer<typeof createOrderRouteType>) => {
  return http.post<Order>("/orders", payload).then((response) => response.data);
};

export const createPublic = (payload: z.infer<typeof createOrderRouteType>) => {
  return http
    .post<Order>("/orders/publicForm", payload)
    .then((response) => response.data);
};

export const update = (id: string, payload: Partial<Order>) => {
  return http
    .patch<Order>(`/orders/${id}`, payload)
    .then((response) => response.data);
};

export const createBulkOrders = async (payload: CreateBulkOrdersPayload) => {
  return http.post("/orders/bulk", payload).then((response) => response.data);
};

export const updateBulkOrders = async (payload: UpdateBulkOrdersPayload) => {
  return http.patch("/orders/bulk", payload).then((response) => response.data);
};

export const deleteBulkOrders = async (payload: DeleteBulkOrdersPayload) => {
  return http
    .post("/orders/bulkDelete", payload)
    .then((response) => response.data);
};

interface GetOrderPayload extends AppFilterType {
  limit?: number;
  searchText?: string;
  skip?: number;
  sortParam?: string;
}

export const getPaginatedOrders = async ({
  payload,
  signal,
}: {
  payload: GetOrderPayload;
  signal: AbortSignal;
}) => {
  return http
    .get(
      `/orders/paginated?startDate=${payload.scheduledDateTimeStart}&endDate=${
        payload.scheduledDateTimeEnd
      }&limit=${payload.limit}&skip=${payload.skip}&status=${payload.orderStatus.join(
        ",",
      )}&orderDeliveryType=${payload.orderDeliveryType.join(
        ",",
      )}&driverIds=${payload.assigneeIds.join(
        ",",
      )}&workspaceIds=${payload.workspaceIds.join(
        ",",
      )}&sortColumn=""&groupBy=""${payload.searchText ? `&search=${payload.searchText}` : ""}${payload.sortParam ? `&sort=${payload.sortParam}` : ""}`,
      {
        signal: signal,
      },
    )
    .then((response) => response.data)
    .then((payload: { data: Order[]; totalCount: number }) => {
      const ordersMap: Map<string, Order> = new Map();
      payload.data.forEach((order) => ordersMap.set(order.id, order));
      return {
        data: ordersMap,
        totalCount: payload.totalCount,
      };
    });
};

export const get = async ({
  payload,
  signal,
}: {
  payload: GetOrderPayload;
  signal: AbortSignal;
}) => {
  return http
    .get(
      `/orders?startDate=${payload.scheduledDateTimeStart}&endDate=${
        payload.scheduledDateTimeEnd
      }&status=${payload.orderStatus.join(
        ",",
      )}&orderDeliveryType=${payload.orderDeliveryType.join(
        ",",
      )}&driverIds=${payload.assigneeIds.join(
        ",",
      )}&workspaceIds=${payload.workspaceIds.join(
        ",",
      )}&stateIds=${payload.stateIds.join(
        ",",
      )}&sortColumn=""&groupBy=""${payload.searchText ? `&search=${payload.searchText}` : ""}${payload.sortParam ? `&sort=${payload.sortParam}` : ""}`,
      {
        signal: signal,
      },
    )
    .then((response) => response.data)
    .then((orders: Order[]) => {
      const ordersMap: Map<string, Order> = new Map();
      orders.forEach((order) => ordersMap.set(order.id, order));
      return ordersMap;
    });
};

export const getOne = async (id: string) => {
  return http.get<Order>(`/orders/${id}`).then((response) => response.data);
};
export const copy = (id: string) => {
  return http
    .post<Order>(`/orders/${id}/copy`)
    .then((response) => response.data);
};

export const deleteOrder = (id: string) => {
  return http.delete(`/orders/${id}`).then((response) => response.data);
};

export const getTrackingOrder = (id: string) => {
  return http.get(`/tracking/orders/${id}`).then((response) => response.data);
};

export const updateTrackingOrder = (
  id: string,
  payload: {
    dropoff?: { note: string };
    phoneNumber: string;
    pickup?: { note: string };
  },
) => {
  return http
    .patch(`/tracking/orders/${id}`, payload)
    .then((response) => response.data);
};

export const verifyTrackingOrderPhone = (
  id: string,
  payload: { countryCode: string; phoneNumber: string },
) => {
  return http
    .post<{ verified: boolean }>(`/tracking/orders/${id}/verify-phone`, payload)
    .then((response) => response.data);
};

export const exportOrders = async (
  payload: AppFilterType,
  visibleColumns: string[],
) => {
  return http
    .post<string>(
      `/orders/export?startDate=${payload.scheduledDateTimeStart}&endDate=${
        payload.scheduledDateTimeEnd
      }&status=${payload.orderStatus.join(
        ",",
      )}&orderDeliveryType=${payload.orderDeliveryType.join(
        ",",
      )}&driverIds=${payload.assigneeIds.join(
        ",",
      )}&workspaceIds=${payload.workspaceIds.join(
        ",",
      )}&sortColumn=""&groupBy=""&sortBy=""&visibleColumns=${visibleColumns.join(
        ",",
      )}`,
    )
    .then((response) => response.data);
};

export const getOrdersCount = async ({
  payload,
  signal,
}: {
  payload: GetOrderPayload;
  signal: AbortSignal;
}) => {
  if (payload.stateIds.length === 0) {
    return { totalCount: 0 };
  }

  return http
    .get<{ totalCount: number }>(
      `/orders/count?startDate=${payload.scheduledDateTimeStart}&endDate=${
        payload.scheduledDateTimeEnd
      }&status=${payload.orderStatus.join(
        ",",
      )}&orderDeliveryType=${payload.orderDeliveryType.join(
        ",",
      )}&driverIds=${payload.assigneeIds.join(
        ",",
      )}&workspaceIds=${payload.workspaceIds.join(
        ",",
      )}&stateIds=${payload.stateIds.join(
        ",",
      )}${payload.searchText ? `&search=${payload.searchText}` : ""}`,
      {
        signal: signal,
      },
    )
    .then((response) => response.data);
};

export const downloadBulkTemplate = async (importType: ImportType) => {
  return http
    .get<string>(`/downloadBulkTemplate/${importType}`, {
      responseType: "blob",
    })
    .then((response) => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;

      const contentDisposition = response.headers["content-disposition"];
      let fileName =
        importType === "INDIVIDUAL"
          ? "Import_Sheet_Template.xlsx"
          : "Route_Based_Import_Sheet_Template.xlsx";

      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="(.+)"/);
        if (fileNameMatch && fileNameMatch[1]) fileName = fileNameMatch[1];
      }
      link.download = fileName;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    });
};
